import { useState, useCallback } from "react";
import {
  PASSWORD_MISMATCH_ERROR,
  PASSWORD_STRENGTH_ERROR,
  isPasswordStrong,
  isPasswordValidationError,
  isPasswordFormReady,
} from "../tools/passwordValidation";

const usePasswordValidation = () => {
  const [error, setError] = useState("");

  const validatePasswordStrength = useCallback((nextPassword: string) => {
    if (!nextPassword) {
      setError((prev) => (prev === PASSWORD_STRENGTH_ERROR ? "" : prev));
      return false;
    }
    if (!isPasswordStrong(nextPassword)) {
      setError(PASSWORD_STRENGTH_ERROR);
      return false;
    }
    setError((prev) => (prev === PASSWORD_STRENGTH_ERROR ? "" : prev));
    return true;
  }, []);

  const validatePasswordsMatch = useCallback(
    (nextPassword: string, nextPasswordConfirm: string) => {
      if (!nextPassword || !nextPasswordConfirm) {
        setError((prev) => (prev === PASSWORD_MISMATCH_ERROR ? "" : prev));
        return true;
      }
      if (nextPassword !== nextPasswordConfirm) {
        setError(PASSWORD_MISMATCH_ERROR);
        return false;
      }
      setError((prev) => (prev === PASSWORD_MISMATCH_ERROR ? "" : prev));
      return true;
    },
    []
  );

  const clearNonValidationError = useCallback(() => {
    setError((prev) => (isPasswordValidationError(prev) ? prev : ""));
  }, []);

  const onPasswordChange = useCallback(
    (
      nextPassword: string,
      passwordConfirm: string,
      setPassword: (value: string) => void
    ) => {
      clearNonValidationError();
      setPassword(nextPassword);
      if (validatePasswordStrength(nextPassword)) {
        validatePasswordsMatch(nextPassword, passwordConfirm);
      }
    },
    [clearNonValidationError, validatePasswordStrength, validatePasswordsMatch]
  );

  const onPasswordConfirmChange = useCallback(
    (
      password: string,
      nextPasswordConfirm: string,
      setPasswordConfirm: (value: string) => void
    ) => {
      clearNonValidationError();
      setPasswordConfirm(nextPasswordConfirm);
      if (validatePasswordStrength(password)) {
        validatePasswordsMatch(password, nextPasswordConfirm);
      }
    },
    [clearNonValidationError, validatePasswordStrength, validatePasswordsMatch]
  );

  const onPasswordBlur = useCallback(
    (password: string, passwordConfirm: string) => {
      if (validatePasswordStrength(password)) {
        validatePasswordsMatch(password, passwordConfirm);
      }
    },
    [validatePasswordStrength, validatePasswordsMatch]
  );

  const validateBeforeSubmit = useCallback(
    (password: string, passwordConfirm: string) => {
      if (!validatePasswordStrength(password)) {
        return false;
      }
      return validatePasswordsMatch(password, passwordConfirm);
    },
    [validatePasswordStrength, validatePasswordsMatch]
  );

  return {
    error,
    setError,
    PASSWORD_STRENGTH_ERROR,
    validatePasswordStrength,
    validatePasswordsMatch,
    validateBeforeSubmit,
    onPasswordChange,
    onPasswordConfirmChange,
    onPasswordBlur,
    isPasswordFormReady,
    isPasswordStrong,
  };
};

export default usePasswordValidation;
